import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import utils from '../../utils/utils';
import MultiSelect from '../../components/customFields/multiSelect';
import AdminRoutePlanV2RouteSetupPickupSlot from './adminRoutePlanV2RouteSetupPickupSlot';
import { faClock, faRoad, faSync, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRoutePlanSetupEdit } from './apiHooks';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';

const AdminRoutePlanV2RouteSetup = ({
  routePlanSetup,
  deliveryDayTimeRanges,
  onRemove,
  onDeliveryDayTimeRangeSelect,
  onPickupSlotAdd,
  onPickupSlotRemove,
  onPickupSlotChange,
}) => {
  const {
    editRoutePlanSetup,
    isLoading: editLoading,
    isError: isEditError,
    error: editError,
  } = useRoutePlanSetupEdit(() => {});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [editError]);

  const deliveryDayTimeRangeOptions = deliveryDayTimeRanges.map((ddtr) => ({
    value: ddtr.id,
    label: `${utils.getFormattedTimeRange(ddtr)} ${ddtr.serviceAreaName}`,
  }));

  const { formatMessage } = useIntl();

  return (
    <div className="form-block m-b-3">
      <Loader show={editLoading} />
      {isEditError ? <ErrorMessage error={utils.getErrorMessageFromResponse(editError)} /> : ''}

      <h3>{`#${routePlanSetup.id}`}</h3>

      <div className="row">
        <div className="col-12 col-sm-6">
          <div className="form-group">
            <MultiSelect
              schema={{
                title: formatMessage({
                  id: 'admin.route.plan.setups.setup.field.delivery.day.time.ranges',
                }),
                options: deliveryDayTimeRangeOptions,
              }}
              onChange={(value) => onDeliveryDayTimeRangeSelect(value)}
              formData={routePlanSetup.deliveryDayTimeRanges}
            />
          </div>
        </div>
      </div>

      <h4>
        <FormattedMessage id={'admin.route.plan.setups.setup.pickup.slots'} />
      </h4>
      {routePlanSetup.pickupSlots.map((pickupSlot, index) => (
        <AdminRoutePlanV2RouteSetupPickupSlot
          key={index}
          pickupSlot={pickupSlot}
          onPickupSlotRemove={() => onPickupSlotRemove(index)}
          onChange={(newValue) => onPickupSlotChange(index, newValue)}
        />
      ))}

      <div className="m-b-3">
        <button className="btn btn-normal-link btn-small" onClick={onPickupSlotAdd}>
          <FontAwesomeIcon icon={faClock} /> <FormattedMessage id={'admin.route.plan.setups.setup.add.pickup.slot'} />
        </button>
      </div>

      <div className="text-right">
        <button
          className="btn btn-normal-link btn-small"
          onClick={() => {
            if (window.confirm(formatMessage({ id: 'admin.route.plan.setups.setup.remove.confirm' }))) {
              onRemove();
            }
          }}
        >
          <FontAwesomeIcon icon={faTrash} /> <FormattedMessage id={'admin.route.plan.setups.setup.remove'} />
        </button>
      </div>

      <div className="">
        {['IN_QUEUE', 'IN_PROGRESS'].indexOf(routePlanSetup.routeOptimisationResult?.status) !== -1 ? (
          <button
            onClick={() => editRoutePlanSetup({ routePlanSetup, refreshRouteOptimisationStatus: true })}
            className={`btn btn-default btn-small ${editLoading ? 'btn-loading' : ''}`}
          >
            <FontAwesomeIcon icon={faSync} /> <FormattedMessage id={'admin.route.plan.setups.refresh.route.plans'} />
          </button>
        ) : routePlanSetup.routeOptimisationResult ? (
          <button
            onClick={() => {
              if (window.confirm(formatMessage({ id: 'admin.route.plan.setups.delete.route.plans.confirm' }))) {
                editRoutePlanSetup({ routePlanSetup, deleteRoutePlans: true });
              }
            }}
            className={`btn btn-danger btn-small ${editLoading ? 'btn-loading' : ''}`}
          >
            <FontAwesomeIcon icon={faTrash} /> <FormattedMessage id={'admin.route.plan.setups.delete.route.plans'} />
          </button>
        ) : (
          <button
            onClick={() => {
              if (window.confirm(formatMessage({ id: 'admin.route.plan.setups.optimise.confirm' }))) {
                editRoutePlanSetup({ routePlanSetup, optimise: true });
              }
            }}
            className={`btn btn-default btn-small ${editLoading ? 'btn-loading' : ''}`}
            disabled={!routePlanSetup.id}
          >
            <FontAwesomeIcon icon={faRoad} /> <FormattedMessage id={'admin.route.plan.setups.optimise'} />
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminRoutePlanV2RouteSetup;
